import { useEffect, useMemo, useState } from "react";
import "../styles/adminstyles/admin.css";

const API_URL = "/api/bands";

const ERA_OPTIONS = [
  "1970s",
  "1980s",
  "1990s",
  "2000s",
  "2010s",
  "2020s",
];

const GENRE_OPTIONS = [
  "Nu Metal",
  "Rap Metal",
  "Rap Rock",
  "Alternative Metal",
  "Alternative Rock",
  "Hard Rock",
  "Christian Rock",
  "Metal",
  "Metalcore",
  "Post-Grunge",
  "Post-Hardcore",
  "Reggae Rock",
  "Modern Rock",
];

const emptyBand = {
  id: null,
  created_at: "",
  name: "",
  slug: "",
  era: [],
  subgenres: [],
  location: "",
  years_active: "",
  description: "",
  image_path: null,

  website: "",
  instagram: "",
  facebook: "",
  x: "",
  youtube: "",
  spotify: "",
  apple_music: "",
  tiktok: "",
  link_tree: "",

  image_url: "",

  christian: true,
  featured: false,
  active: true,
};

const createSlug = (value = "") => {
  return value
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

const AdminBandsScreen = () => {
  const [bands, setBands] = useState([]);
  const [formData, setFormData] = useState(emptyBand);

  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);

  //
  // LOAD BANDS
  //

  useEffect(() => {
    loadBands();
  }, []);

  const loadBands = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error("Could not load bands.");
      }

      const data = await response.json();

      setBands(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("Could not load bands.");
    } finally {
      setLoading(false);
    }
  };

  //
  // SEARCH
  //

  const filteredBands = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) {
      return [...bands].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    }

    return bands
      .filter((band) => {
        const name = band.name || "";
        const location = band.location || "";
        const genres = Array.isArray(band.subgenres)
          ? band.subgenres.join(" ")
          : "";

        return `${name} ${location} ${genres}`
          .toLowerCase()
          .includes(search);
      })
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
  }, [bands, searchTerm]);

  //
  // FORM HELPERS
  //

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNameChange = (event) => {
    const value = event.target.value;

    setFormData((previous) => {
      const shouldUpdateSlug =
        !editingId ||
        !previous.slug ||
        previous.slug === createSlug(previous.name);

      return {
        ...previous,
        name: value,
        slug: shouldUpdateSlug
          ? createSlug(value)
          : previous.slug,
      };
    });
  };

  const handleArrayCheckbox = (field, value) => {
    setFormData((previous) => {
      const current = Array.isArray(previous[field])
        ? previous[field]
        : [];

      const exists = current.includes(value);

      return {
        ...previous,
        [field]: exists
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  //
  // NEW BAND
  //

  const startNewBand = () => {
    setEditingId(null);
    setSelectedImage(null);
    setFormData(emptyBand);
    setMessage("");
    setError("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  //
  // EDIT BAND
  //

  const editBand = (band) => {
    setEditingId(band.id);

    setFormData({
      ...emptyBand,
      ...band,

      era: Array.isArray(band.era)
        ? band.era
        : [],

      subgenres: Array.isArray(band.subgenres)
        ? band.subgenres
        : [],
    });

    setSelectedImage(null);
    setMessage("");
    setError("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  //
  // CANCEL EDIT
  //

  const cancelEdit = () => {
    setEditingId(null);
    setSelectedImage(null);
    setFormData(emptyBand);
    setMessage("");
    setError("");
  };

  //
  // GET NEXT ID
  //

  const getNextId = () => {
    if (!bands.length) {
      return 1;
    }

    return (
      Math.max(
        ...bands.map((band) => Number(band.id) || 0)
      ) + 1
    );
  };

  //
  // UPLOAD IMAGE
  //

  const uploadImage = async (slug) => {
    if (!selectedImage) {
      return formData.image_url || "";
    }

    const imageFormData = new FormData();

    imageFormData.append("image", selectedImage);
    imageFormData.append("slug", slug);

    const response = await fetch(
      "/api/bands/upload-image",
      {
        method: "POST",
        body: imageFormData,
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.error || "Could not upload image."
      );
    }

    return result.image_url;
  };

  //
  // SAVE ALL BANDS TO JSON
  //

  const saveBandsToFile = async (updatedBands) => {
    const response = await fetch(API_URL, {
      method: "PUT",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(updatedBands),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.error || "Could not save bands."
      );
    }

    return result;
  };

  //
  // SUBMIT FORM
  //

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const cleanName = formData.name.trim();

      if (!cleanName) {
        throw new Error("Band name is required.");
      }

      const cleanSlug =
        formData.slug.trim() ||
        createSlug(cleanName);

      if (!cleanSlug) {
        throw new Error("A valid slug is required.");
      }

      const duplicateSlug = bands.find(
        (band) =>
          band.slug === cleanSlug &&
          band.id !== editingId
      );

      if (duplicateSlug) {
        throw new Error(
          `The slug "${cleanSlug}" is already being used by ${duplicateSlug.name}.`
        );
      }

      //
      // IMAGE
      //

      let imageUrl = formData.image_url || "";

      if (selectedImage) {
        setUploading(true);

        imageUrl = await uploadImage(cleanSlug);

        setUploading(false);
      }

      //
      // BUILD BAND OBJECT
      //

      const bandToSave = {
        ...formData,

        id: editingId || getNextId(),

        created_at:
          formData.created_at ||
          new Date().toISOString(),

        name: cleanName,
        slug: cleanSlug,

        era: Array.isArray(formData.era)
          ? formData.era
          : [],

        subgenres: Array.isArray(
          formData.subgenres
        )
          ? formData.subgenres
          : [],

        image_url: imageUrl,

        christian: Boolean(formData.christian),
        featured: Boolean(formData.featured),
        active: Boolean(formData.active),
      };

      let updatedBands;

      //
      // EDIT EXISTING BAND
      //

      if (editingId) {
        updatedBands = bands.map((band) =>
          band.id === editingId
            ? bandToSave
            : band
        );
      }

      //
      // ADD NEW BAND
      //

      else {
        updatedBands = [
          ...bands,
          bandToSave,
        ];
      }

      //
      // WRITE TO bands.json
      //

      await saveBandsToFile(updatedBands);

      setBands(updatedBands);

      setMessage(
        editingId
          ? `${cleanName} updated successfully.`
          : `${cleanName} added successfully.`
      );

      setEditingId(null);
      setSelectedImage(null);
      setFormData(emptyBand);
    } catch (err) {
      console.error(err);

      setError(
        err.message ||
          "Something went wrong while saving."
      );
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  //
  // DELETE BAND
  //

  const deleteBand = async (band) => {
    const confirmed = window.confirm(
      `Delete ${band.name}?\n\nThis will remove the band from bands.json.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setMessage("");

      const updatedBands = bands.filter(
        (item) => item.id !== band.id
      );

      await saveBandsToFile(updatedBands);

      setBands(updatedBands);

      if (editingId === band.id) {
        cancelEdit();
      }

      setMessage(
        `${band.name} was deleted.`
      );
    } catch (err) {
      console.error(err);

      setError(
        err.message ||
          "Could not delete band."
      );
    }
  };

  //
  // RENDER
  //

  return (
    <section className="admin-page py-5">
      <div className="container">

        <div className="admin-header mb-5">
          <div>
            <p className="section-kicker">
              Nu Metal Kingdom
            </p>

            <h1>Band Editor</h1>

            <p>
              Manage the bands stored in{" "}
              <strong>
                src/data/bands.json
              </strong>
            </p>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={startNewBand}
          >
            + Add Band
          </button>
        </div>

        {message && (
          <div className="alert alert-success">
            {message}
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {/* =========================
            BAND FORM
        ========================== */}

        <div className="admin-card mb-5">
          <h2>
            {editingId
              ? `Edit ${formData.name}`
              : "Add Band"}
          </h2>

          <form onSubmit={handleSubmit}>

            <div className="row">

              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Band Name
                </label>

                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Slug
                </label>

                <input
                  type="text"
                  name="slug"
                  className="form-control"
                  value={formData.slug}
                  onChange={handleChange}
                />

                <small>
                  Example: thousand-foot-krutch
                </small>
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Location
                </label>

                <input
                  type="text"
                  name="location"
                  className="form-control"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Years Active
                </label>

                <input
                  type="text"
                  name="years_active"
                  className="form-control"
                  value={formData.years_active}
                  onChange={handleChange}
                  placeholder="1992–present"
                />
              </div>

            </div>

            {/* ERA */}

            <div className="mb-4">
              <label className="form-label">
                Era
              </label>

              <div className="admin-checkbox-group">
                {ERA_OPTIONS.map((era) => (
                  <label
                    key={era}
                    className="me-4"
                  >
                    <input
                      type="checkbox"
                      checked={formData.era.includes(
                        era
                      )}
                      onChange={() =>
                        handleArrayCheckbox(
                          "era",
                          era
                        )
                      }
                    />{" "}
                    {era}
                  </label>
                ))}
              </div>
            </div>

            {/* GENRES */}

            <div className="mb-4">
              <label className="form-label">
                Genres / Subgenres
              </label>

              <div className="admin-checkbox-group">
                {GENRE_OPTIONS.map((genre) => (
                  <label
                    key={genre}
                    className="me-4 mb-2"
                  >
                    <input
                      type="checkbox"
                      checked={formData.subgenres.includes(
                        genre
                      )}
                      onChange={() =>
                        handleArrayCheckbox(
                          "subgenres",
                          genre
                        )
                      }
                    />{" "}
                    {genre}
                  </label>
                ))}
              </div>
            </div>

            {/* DESCRIPTION */}

            <div className="mb-4">
              <label className="form-label">
                Description
              </label>

              <textarea
                name="description"
                className="form-control"
                rows="12"
                value={formData.description}
                onChange={handleChange}
                placeholder="<p>Band description...</p>"
              />
            </div>

            {/* IMAGE */}

            <div className="mb-4">
              <label className="form-label">
                Band Image
              </label>

              {formData.image_url && (
                <div className="mb-3">
                  <img
                    src={formData.image_url}
                    alt={formData.name}
                    style={{
                      maxWidth: "250px",
                      width: "100%",
                      height: "auto",
                    }}
                  />

                  <p className="mt-2">
                    <code>
                      {formData.image_url}
                    </code>
                  </p>
                </div>
              )}

              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={(event) =>
                  setSelectedImage(
                    event.target.files?.[0] ||
                      null
                  )
                }
              />

              {selectedImage && (
                <p className="mt-2">
                  New image:{" "}
                  <strong>
                    {selectedImage.name}
                  </strong>
                </p>
              )}

              <small>
                Images are saved to
                public/assets/bands/.
              </small>
            </div>

            {/* SOCIAL LINKS */}

            <hr />

            <h3 className="mb-4">
              Website & Social Links
            </h3>

            <div className="row">

              {[
                ["website", "Website"],
                ["instagram", "Instagram"],
                ["facebook", "Facebook"],
                ["x", "X"],
                ["youtube", "YouTube"],
                ["spotify", "Spotify"],
                ["apple_music", "Apple Music"],
                ["tiktok", "TikTok"],
                ["link_tree", "Linktree"],
              ].map(([field, label]) => (
                <div
                  key={field}
                  className="col-12 col-md-6 mb-3"
                >
                  <label className="form-label">
                    {label}
                  </label>

                  <input
                    type="url"
                    name={field}
                    className="form-control"
                    value={
                      formData[field] || ""
                    }
                    onChange={handleChange}
                    placeholder="https://"
                  />
                </div>
              ))}

            </div>

            {/* FLAGS */}

            <hr />

            <div className="admin-checkbox-group mb-4">

              <label className="me-4">
                <input
                  type="checkbox"
                  name="christian"
                  checked={formData.christian}
                  onChange={handleChange}
                />{" "}
                Christian
              </label>

              <label className="me-4">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />{" "}
                Featured
              </label>

              <label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />{" "}
                Active
              </label>

            </div>

            {/* BUTTONS */}

            <div className="d-flex gap-3">

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {uploading
                  ? "Uploading Image..."
                  : saving
                  ? "Saving..."
                  : editingId
                  ? "Update Band"
                  : "Add Band"}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}

            </div>

          </form>
        </div>

        {/* =========================
            BAND LIST
        ========================== */}

        <div className="admin-card">

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              Bands ({bands.length})
            </h2>

            <input
              type="search"
              className="form-control"
              style={{
                maxWidth: "350px",
              }}
              placeholder="Search bands..."
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
            />
          </div>

          {loading ? (
            <p>Loading bands...</p>
          ) : filteredBands.length === 0 ? (
            <p>No bands found.</p>
          ) : (
            <div className="row">

              {filteredBands.map((band) => (
                <div
                  key={band.id}
                  className="col-12 col-md-6 col-xl-4 mb-4"
                >
                  <div className="admin-band-card h-100">

                    {band.image_url && (
                      <img
                        src={band.image_url}
                        alt={band.name}
                        className="admin-band-image"
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          objectFit: "cover",
                        }}
                      />
                    )}

                    <div className="p-3">

                      <h3>{band.name}</h3>

                      <p>
                        <code>
                          {band.slug}
                        </code>
                      </p>

                      {band.location && (
                        <p>
                          {band.location}
                        </p>
                      )}

                      {Array.isArray(
                        band.subgenres
                      ) &&
                        band.subgenres.length >
                          0 && (
                          <p>
                            {band.subgenres.join(
                              " • "
                            )}
                          </p>
                        )}

                      <div className="mb-3">

                        {band.featured && (
                          <span className="badge bg-warning text-dark me-2">
                            Featured
                          </span>
                        )}

                        {band.active ? (
                          <span className="badge bg-success">
                            Active
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            Inactive
                          </span>
                        )}

                      </div>

                      <div className="d-flex gap-2">

                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() =>
                            editBand(band)
                          }
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() =>
                            deleteBand(band)
                          }
                        >
                          Delete
                        </button>

                      </div>

                    </div>
                  </div>
                </div>
              ))}

            </div>
          )}

        </div>

      </div>
    </section>
  );
};

export default AdminBandsScreen;